import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  FilterBar,
  FilterField,
  Input,
  PageShell,
  Pagination,
  RadixSelect,
  Skeleton,
  type SelectOption,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { useAdminAccountsQuery } from '../hooks/useAdminAccountsQuery';
import { useAdminAccountMutations } from '../hooks/useAdminAccountMutations';
import { useAdminRolesQuery } from '../hooks/useAdminRolesQuery';
import { buildAdminAccountColumns } from '../components/AdminAccountTable';
import { AdminRoleSelectDialog } from '../components/AdminRoleSelectDialog';
import { RoleMenusDialog } from '../components/RoleMenusDialog';
import { AdminAccountDeactivateDialog } from '../components/AdminAccountDeactivateDialog';
import { ADMIN_WRITE_ROLES } from '../lib/adminActionRbac';
import { isDisplayableRole } from '../types/admin';
import type { AdminAccountSummary, AdminRole } from '../types/admin';

const PAGE_SIZE = 20;
const ALL_VALUE = '';

export const AdminAccountListPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const canWrite = useHasRole(ADMIN_WRITE_ROLES);
  const isSuperAdmin = useHasRole(['SUPER_ADMIN']);
  const [roleMenusOpen, setRoleMenusOpen] = useState(false);

  const [keyword, setKeyword] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [role, setRole] = useState<AdminRole | ''>('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const [rolesTarget, setRolesTarget] = useState<AdminAccountSummary | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<AdminAccountSummary | null>(null);

  const rolesQuery = useAdminRolesQuery();
  const { replaceRoles, deactivate } = useAdminAccountMutations();

  const query = useAdminAccountsQuery({
    keyword: keyword || undefined,
    role: role || null,
    status: status || null,
    page,
    size: PAGE_SIZE,
  });

  const items = query.data?.items ?? [];
  const total = query.data?.meta.totalItems ?? 0;

  const handleSearch = useCallback(() => {
    setKeyword(inputValue);
    setPage(1);
  }, [inputValue]);

  const handleReset = useCallback(() => {
    setInputValue('');
    setKeyword('');
    setRole('');
    setStatus('');
    setPage(1);
  }, []);

  const roleOptions = useMemo<ReadonlyArray<SelectOption>>(() => {
    const views = (rolesQuery.data ?? []).filter((r) => isDisplayableRole(r.code));
    return [
      { value: ALL_VALUE, label: t('admin.accounts.filter.roleAll') },
      ...views.map((r) => ({ value: r.code, label: t(`admin.roles.codes.${r.code}`, r.code) })),
    ];
  }, [rolesQuery.data, t]);

  const statusOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: ALL_VALUE, label: t('admin.accounts.filter.statusAll') },
      { value: 'ACTIVE', label: t('admin.accounts.status.ACTIVE') },
      { value: 'INACTIVE', label: t('admin.accounts.status.INACTIVE') },
    ],
    [t],
  );

  const columns = useMemo(
    () =>
      buildAdminAccountColumns({
        t,
        canWrite,
        onEdit: (adminUserId) => navigate(`/admin/accounts/${adminUserId}`),
        onChangeRoles: (account) => setRolesTarget(account),
        onDeactivate: (account) => setDeactivateTarget(account),
      }),
    [t, canWrite, navigate],
  );

  return (
    <PageShell
      title={t('admin.accounts.list.title')}
      data-testid="admin-account-list-page"
      actions={
        canWrite || isSuperAdmin ? (
          <div className="flex items-center gap-2">
            {/* QA-514 역할별 접근 메뉴 — 최고관리자만. */}
            {isSuperAdmin ? (
              <Button size="sm" variant="outline" onClick={() => setRoleMenusOpen(true)} data-testid="role-menus-open">
                역할 권한 관리
              </Button>
            ) : null}
            {canWrite ? (
              <Button size="sm" onClick={() => navigate('/admin/accounts/new')}>
                {t('admin.accounts.list.new')}
              </Button>
            ) : null}
          </div>
        ) : undefined
      }
    >
      <RoleMenusDialog open={roleMenusOpen} onOpenChange={setRoleMenusOpen} />
      <FilterBar
        dense
        filters={
          <>
            <FilterField label={t('admin.accounts.filter.keyword')}>
              <Input
                type="search"
                size="sm"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                placeholder={t('admin.accounts.filter.keywordPlaceholder')}
                aria-label={t('admin.accounts.filter.keywordPlaceholder')}
              />
            </FilterField>
            <FilterField label={t('admin.accounts.cols.roles')}>
              <RadixSelect
                value={role}
                onChange={(v) => {
                  setRole(v as AdminRole | '');
                  setPage(1);
                }}
                options={roleOptions}
                aria-label={t('admin.accounts.cols.roles')}
              />
            </FilterField>
            <FilterField label={t('admin.accounts.cols.status')}>
              <RadixSelect
                value={status}
                onChange={(v) => {
                  setStatus(v);
                  setPage(1);
                }}
                options={statusOptions}
                aria-label={t('admin.accounts.cols.status')}
              />
            </FilterField>
          </>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={handleSearch}>
              {t('admin.common.search')}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleReset}>
              {t('admin.common.reset')}
            </Button>
          </>
        }
      />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-[36px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('admin.accounts.list.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('admin.common.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={[...columns]}
          rows={[...items]}
          rowKey={(row) => row.adminUserId}
          stickyHeader
          mobileCardCollapse
          emptyState={t('admin.accounts.list.empty')}
          ariaLabel={t('admin.accounts.list.title')}
        />
      )}

      <Pagination
        total={total}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        className="mt-4"
      />

      {rolesTarget ? (
        <AdminRoleSelectDialog
          open
          onOpenChange={(o) => {
            if (!o) setRolesTarget(null);
          }}
          roleViews={rolesQuery.data ?? []}
          value={rolesTarget.roles}
          busy={replaceRoles.isPending}
          onApply={(roles) => {
            void replaceRoles
              .mutateAsync({ adminUserId: rolesTarget.adminUserId, body: { roles: [...roles] } })
              .finally(() => setRolesTarget(null));
          }}
        />
      ) : null}

      {deactivateTarget ? (
        <AdminAccountDeactivateDialog
          open
          onOpenChange={(o) => {
            if (!o) setDeactivateTarget(null);
          }}
          accountName={deactivateTarget.name}
          busy={deactivate.isPending}
          onConfirm={() => {
            void deactivate
              .mutateAsync(deactivateTarget.adminUserId)
              .finally(() => setDeactivateTarget(null));
          }}
        />
      ) : null}
    </PageShell>
  );
};
